import type { AttestedLinkRelation, TraceQuestion, TraceAllocationCapability, TraceInsights, TraceReceiptSeed } from "@authe/fe-commons/flows";
import type { InteractionGraphDTO } from "@authe/fe-commons/models";
import type { traceFiltersToWire } from "@/api/tracersSource";

export type TraceConfig = ReturnType<typeof traceFiltersToWire> & {
  question?: TraceQuestion;
  read_limit?: number;
  seeds: string[];
  direction: "outbound" | "inbound" | "both";
  max_hops: number;
  alarms: string[];
  listed: string[];
  /** Addresses linked by hand (`party@network`): the reader walks from them and draws the link. */
  links?: LocalTraceLink[];
};
/** An asserted link as the reader takes it; `start` is where it leaves the trail (none: an added address). */
export type LocalTraceLink = {
  address: string; start?: string | null; relation: AttestedLinkRelation; note?: string | null; tx_hash?: string | null;
  receipts?: TraceReceiptSeed[];
};
export type LocalTransaction = {
  evidence_kind?: "historical_recording" | "simulation" | "local_recording";
  id: string;
  tx_hash: string;
  chain_id: string;
  block_number: number;
  timestamp: number;
  label: string;
  finality?: string;
  from_address?: string;
  to_address?: string;
};
/**
 * What a campaign says one send's message journey must be, resolved by the
 * reader to the verified message the send label (`<step>:<label>`) and the
 * message ordinal name (`journeycheck.pick`): `message_id` once the journeys
 * are ready and the message verified, `problems` what differs, `outcome`
 * `ok` / `fail` / `gap` (a known gap still open) / `stale` (a gap note that no
 * longer holds); both null while the journeys are still being verified.
 */
export type JourneyExpectation = {
  send: string;
  message?: number | null;
  attempts: string[];
  state: string;
  callback?: string | null;
  recovery_observed: boolean;
  note?: string;
  known_gap?: string | null;
  message_id?: string | null;
  /** The chain the resolved message was sent on: with its id, the message's identity. */
  source_chain?: string | null;
  problems?: string[];
  outcome?: "ok" | "fail" | "gap" | "stale" | null;
};
export type Firing = {
  alarm: string;
  tx_hash: string;
  chain: string;
  finality?: string;
  label: string;
};
/** One thing the platform's situation engine raised over the chains' recorded runs. */
export type LocalSituation = {
  id: string | null;
  finding_id?: string | null;
  signal: string | null;
  transactions: { id: string | null; tx_hash: string; chain_id: string | null; label: string }[];
  subjects: { address: string; chain_id?: string | null; role: string; accused?: boolean; basis?: string }[];
};
export type Snapshot = {
  funds_review?: import("@authe/fe-commons/funds").FundsReview | null;
  replay?: { id: string; title: string; selected_step: string; pending?: boolean; steps: {
    id: string; title: string; description: string; kind: "historical_recording" | "simulation";
    available: boolean; chains: string[];
  }[] } | null;
  provenance?: { case: string; title?: string; kind: "historical_recording" | "simulation"; description: string;
    limitations: string[]; pending_legs: number; source?: string | null } | null;
  session_id: string;
  revision: number;
  state: "starting" | "updating" | "ready" | "error";
  reader_progress?: { phase: "reading" | "queued" | "normalizing" | "decoding" | "ready"; processed: number; total: number } | null;
  error?: string;
  issue?: { code: "networks_stopped"; networks: string[] };
  updated_at?: number;
  config: TraceConfig;
  limits?: { max_hops: number; max_read_limit?: number; allocation_methods?: TraceAllocationCapability[] };
  networks: { name: string; chain_id: string; kind: string; rpc_url: string }[];
  /** Registry rows for chains only the recorded runs know (a protocol network's Ethereum, a NEAR localnet). */
  chains?: Record<string, unknown>[];
  heads?: Record<string, number>;
  /** Highest transaction block in the reader, separate from live/finality heads. */
  observed_blocks?: Record<string, number>;
  transaction_count?: number;
  transaction_refs?: Record<string, string>;
  transactions?: LocalTransaction[];
  situations?: { state: "idle" | "reading" | "ready" | "error" | "not_evaluated"; raised: LocalSituation[]; errors?: string[]; reason?: string };
  /**
   * What the reader can read contract state on: live chains at any block, recorded runs at their
   * recorded transactions' blocks (the platform's own contract-state reader, wired to the reader).
   */
  contract_state?: { live: string[]; recorded: string[]; scope?: string } | null;
  /** Verified message journeys over the reader's decoded transactions (the shared journey compiler's rows). */
  journeys?: {
    state: "idle" | "reading" | "ready" | "error";
    contexts?: unknown[];
    messages?: number;
    rejected?: { message_id: string; source_tx: string; reason: string }[];
    /** `CAIP-2:hash` → this reader's transaction id, for local inspection. */
    local_refs?: Record<string, string>;
    errors?: string[];
    /** A campaign's `journeys:` expectations (`bc dashboard --campaign`), resolved by the reader. */
    expectations?: JourneyExpectation[];
  };
  labels?: Record<string, string>;
  graph?: InteractionGraphDTO | null;
  token_metadata?: { address: string; chainId: string; decimals?: number; symbol?: string; name?: string }[];
  /** Scenario references resolved on the selected local ledger, for shared forms. */
  token_addresses?: Record<string, string>;
  trace?: {
    status: string;
    status_reason?: string;
    walks: number;
    resumable?: boolean;
    frontier_count?: number;
    hops_walked?: number;
    continuation_hops_max?: number | null;
    continuation_reason?: string | null;
    insights?: TraceInsights;
    bounded_by?: string[];
    receipts?: Record<string, unknown>[];
    finality?: unknown;
    coverage_gaps?: Record<string, unknown>[];
    lookahead?: { edges?: Record<string, unknown>[] };
    /** The config's links, placed on the walked graph (node ids). */
    links?: { start_node_id: string | null; node_id: string; chain_id: string; address: string;
      relation: AttestedLinkRelation; note?: string | null; tx_hash?: string | null; receipts?: TraceReceiptSeed[] }[];
  } | null;
  waiting?: string[];
  warnings?: string[];
  armed?: { name: string; chain: string; address: string }[];
  alarms?: {
    firings: Firing[];
    expected: { alarm: string; finality: string }[];
    errors: string[];
  };
};

/**
 * A network the reader replays from saved evidence: `recorded` (captured
 * transactions) or `manual` (an analyst-added source on a chain that shows
 * nothing, such as a Monero sender; no transaction at all). Neither is polled.
 */
export function isRecordedNetwork(network: { kind: string }): boolean {
  return network.kind === "recorded" || network.kind === "manual";
}

/** A pairing code can only be sent to a literal loopback origin, never a URL supplied by a site. */
export function agentOrigin(input: string): string {
  const url = new URL(input);
  if (
    url.protocol !== "http:" ||
    !["127.0.0.1", "localhost"].includes(url.hostname) ||
    !url.port ||
    url.username ||
    url.password ||
    url.pathname !== "/" ||
    url.search ||
    url.hash
  ) {
    throw new Error(
      "Use the agent's loopback URL, for example http://127.0.0.1:8788"
    );
  }
  return url.origin;
}

export class LocalAgent {
  readonly origin: string;
  constructor(origin: string, private readonly code: string) {
    this.origin = agentOrigin(origin);
    if (!code.trim())
      throw new Error("Enter the pairing code printed by bc dashboard");
  }
  async request<T>(
    path: string,
    signal?: AbortSignal,
    config?: TraceConfig | { step: string } | Record<string, unknown>,
    method: "PUT" | "POST" = "PUT",
  ): Promise<T> {
    const response = await fetch(`${this.origin}/v1/${path}`, {
      method: config ? method : "GET",
      signal,
      credentials: "omit",
      cache: "no-store",
      redirect: "error",
      headers: {
        Authorization: `Bearer ${this.code}`,
        ...(config ? { "Content-Type": "application/json" } : {}),
      },
      ...(config ? { body: JSON.stringify(config) } : {}),
    });
    const value = await response.json();
    if (!response.ok)
      throw new Error(value.error ?? `Local agent returned ${response.status}`);
    return value as T;
  }
}

export const entries = (value: string): string[] =>
  value
    .split(/[,\n]/)
    .map((v) => v.trim())
    .filter(Boolean);
